import React from "react";
import { Helmet } from "react-helmet-async";
import { ASSETS } from "@/lib/api";

export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://cardsciencehq.com").replace(/\/$/, "");
const DEFAULT_OG = ASSETS.hero;

export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "CardScience HQ",
  alternateName: "CARD HQ",
  url: SITE_URL,
  logo: `${SITE_URL}/logo512.png`,
  description:
    "CardScience HQ is a collectibles preparation laboratory offering card cleaning, preparation, preservation and grading prep for cards and comics. Nationwide mail-in service.",
  areaServed: "US",
  slogan: "The grader makes the call. We make sure your card shows up ready.",
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "CardScience HQ",
  url: SITE_URL,
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const serviceSchema = ({ name, description, path, serviceType }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  serviceType: serviceType || name,
  areaServed: "US",
  provider: { "@type": "Organization", name: "CardScience HQ", url: SITE_URL },
  url: `${SITE_URL}${path}`,
});

export const articleSchema = ({ title, description, path, datePublished }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  datePublished,
  dateModified: datePublished,
  author: { "@type": "Organization", name: "CardScience HQ" },
  publisher: { "@type": "Organization", name: "CardScience HQ", logo: { "@type": "ImageObject", url: `${SITE_URL}/logo512.png` } },
  mainEntityOfPage: `${SITE_URL}${path}`,
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const eventSchema = ({ name, description, path }) => ({
  "@context": "https://schema.org",
  "@type": "Event",
  name,
  description,
  eventAttendanceMode: "https://schema.org/MixedEventAttendanceMode",
  organizer: { "@type": "Organization", name: "CardScience HQ", url: SITE_URL },
  url: `${SITE_URL}${path}`,
});

export const Seo = ({ title, description, path = "/", image, type = "website", jsonLd, noindex = false, includeOrg = false }) => {
  const url = `${SITE_URL}${path}`;
  const img = image || DEFAULT_OG;
  const blocks = [];
  if (includeOrg) blocks.push(orgSchema(), websiteSchema());
  if (jsonLd) Array.isArray(jsonLd) ? blocks.push(...jsonLd) : blocks.push(jsonLd);
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow"} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="CardScience HQ" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Helmet>
  );
};
